import { ASSOCIATION_DIRECTION } from '../../../filterQueryFormat/associations/AssociationUtilities';
import { ObjectTypeIdRegex, isAnyEventTypeId } from '../../../filterQueryFormat/ObjectTypeId';
import { getAssociatedRecordsUtils, getAssociationInfoFromAssociationValue, getAssociationInfoFromPropertySuffix, getAssociationOption, getAssociationOptionByValue, getAssociationUniqueIdentifier } from '../../../utils/associatedRecordsUtils';
import { isFieldRestricted } from '../../../utils/fieldLevelPermissions';
import { restrictedAsset, wrapWithRestrictedAssetTooltip } from '../utils/fieldLevelPermissionsUtilsForQuickFilters';
import { useContext, useMemo } from 'react';
import { useStrategy } from '../../../hooks/useStrategy';
import AssociatedTo from '../../../filterQueryFormat/operator/AssociatedTo';
import EditorRelatedConditionsHeadingAssociationSelectValueComponent from '../../editor/EditorRelatedConditionsHeadingAssociationSelectValueComponent';
import FormattedMessage from 'I18n/components/FormattedMessage';
import QuickFilterSelectBase from './QuickFilterSelectBase';
import QuickFilterTooltip from './QuickFilterTooltip';
import SegmentationFiltersInstanceContext from '../../../state/SegmentationFiltersInstanceContext';
import UISelect from 'UIComponents/input/UISelect';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AssociationLabelQuickFilter = ({
  associatedRecord,
  buttonProperties,
  condition,
  disableTruncation,
  isFirst,
  property,
  onValueChange
}) => {
  const {
    data,
    props: {
      baseFilterFamily
    }
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    getObjectInverseAssociationOptions
  } = useStrategy();
  const {
    getAssociationDefinition,
    getObjectTypeIdFromPropertySuffix
  } = getAssociatedRecordsUtils(data, baseFilterFamily);
  const propertySuffix = (condition === null || condition === void 0 ? void 0 : condition.propertySuffix) || associatedRecord.propertySuffix;
  const objectId = (condition === null || condition === void 0 ? void 0 : condition.value) || associatedRecord.value;
  const associatedObjectTypeId = getObjectTypeIdFromPropertySuffix(propertySuffix);
  const {
    associationId: associationInverseId,
    associationCategory
  } = getAssociationInfoFromPropertySuffix(propertySuffix);
  const associationDefinition = associatedObjectTypeId ? getAssociationDefinition(associatedObjectTypeId, associationInverseId, associationCategory) : undefined;
  const associationOptions = useMemo(() => getObjectInverseAssociationOptions(associatedObjectTypeId) || [], [getObjectInverseAssociationOptions, associatedObjectTypeId]);
  const associationOption = getAssociationOption(associationOptions, associationInverseId, associationCategory, ASSOCIATION_DIRECTION.INVERSE);
  const isActive = !!condition;
  const isRestrictedField = isFieldRestricted(property.accessLevel);
  const isAssociationFilterFamily = associatedObjectTypeId && ObjectTypeIdRegex.test(associatedObjectTypeId) && !isAnyEventTypeId(associatedObjectTypeId);
  const options = useMemo(() => {
    const associationLabelOptions = associationOptions.reduce((acc, option) => {
      if ('options' in option) {
        const labelOptions = option.options.filter(o => !o.isWildcard);
        return [...acc, ...labelOptions];
      }
      if (!option.isWildcard) {
        return [...acc, option];
      }
      return acc;
    }, []);
    return associationLabelOptions.map(option => ({
      text: option.text || option.value,
      value: option.value
    })).sort((a, b) => a.text.localeCompare(b.text));
  }, [associationOptions]);
  if (!isAssociationFilterFamily || !objectId || options.length === 0) {
    return null;
  }
  const propertyLabel = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "customerDataFiltersUI.quickFiltersBar.associationLabel"
  });
  const renderButtonContent = () => {
    const baseContent = /*#__PURE__*/_jsxs(_Fragment, {
      children: [isRestrictedField && restrictedAsset, propertyLabel]
    });
    if (disableTruncation) {
      const contentWithoutTruncation = /*#__PURE__*/_jsx("span", {
        children: baseContent
      });
      return isRestrictedField ? wrapWithRestrictedAssetTooltip(contentWithoutTruncation) : /*#__PURE__*/_jsx(QuickFilterTooltip, {
        label: propertyLabel,
        property: property,
        value: associationDefinition === null || associationDefinition === void 0 ? void 0 : associationDefinition.label,
        children: contentWithoutTruncation
      });
    }
    const useTruncateStringTooltip = !isActive && !property.tooltip;
    const contentWithValue = /*#__PURE__*/_jsx(TruncateString, {
      maxWidth: buttonProperties.LABEL_MAX_WIDTH,
      tooltip: useTruncateStringTooltip && !isRestrictedField,
      children: propertyLabel
    });
    return isRestrictedField ? wrapWithRestrictedAssetTooltip(contentWithValue) : /*#__PURE__*/_jsx(QuickFilterTooltip, {
      disabled: useTruncateStringTooltip,
      label: propertyLabel,
      property: property,
      value: associationDefinition === null || associationDefinition === void 0 ? void 0 : associationDefinition.label,
      children: contentWithValue
    });
  };
  const onChange = evt => {
    const newAssociationValue = evt.target.value;
    const newAssociationOption = getAssociationOptionByValue(associationOptions, newAssociationValue);
    const {
      associationCategory: newAssociationCategory
    } = getAssociationInfoFromAssociationValue(newAssociationOption === null || newAssociationOption === void 0 ? void 0 : newAssociationOption.value);
    const definition = getAssociationDefinition(associatedObjectTypeId, newAssociationOption === null || newAssociationOption === void 0 ? void 0 : newAssociationOption.inverseId, newAssociationCategory);
    if (definition) {
      const associationUniqueIdentifier = getAssociationUniqueIdentifier(definition);
      if (associationUniqueIdentifier) {
        onValueChange(AssociatedTo.of(property, associationUniqueIdentifier, objectId));
      }
    }
  };
  return /*#__PURE__*/_jsx(QuickFilterSelectBase, {
    button: /*#__PURE__*/_jsx(UISelect, {
      ButtonContent: renderButtonContent,
      buttonSize: buttonProperties.BUTTON_SIZE,
      buttonUse: buttonProperties.BUTTON_USE,
      disabled: isRestrictedField || property.disabled,
      menuWidth: "auto",
      onChange: onChange,
      options: options,
      placement: isFirst ? 'bottom right' : 'bottom',
      value: associationOption === null || associationOption === void 0 ? void 0 : associationOption.value,
      valueComponent: EditorRelatedConditionsHeadingAssociationSelectValueComponent
    }),
    buttonHeight: buttonProperties.BUTTON_HEIGHT,
    isActive: isActive,
    onValueChange: onValueChange,
    propertyName: property.name
  });
};
export default AssociationLabelQuickFilter;